import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { useLang } from "../context/LanguageContext";
import { T, pick } from "../lib/translations";
import { matchGuidesInText, matchCitiesInText, ALL_CITIES } from "../lib/relatedContent";
import { getPost, getBootPost, getPosts } from "../lib/blogCache";
import { ArrowLeft, ArrowRight, Calendar, Clock, MapPin, Phone } from "lucide-react";

const FALLBACK_COVER =
  "https://images.unsplash.com/photo-1589994965851-a8f479c573a9?w=1200";

const decodeHtml = (str) => {
  if (!str) return "";
  const el = document.createElement("textarea");
  el.innerHTML = str;
  return el.value;
};

const stripTags = (html) => {
  if (!html) return "";
  const el = document.createElement("div");
  el.innerHTML = html;
  return (el.textContent || el.innerText || "").trim();
};

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang } = useLang();
  const [post, setPost] = useState(() => getBootPost(slug));
  const [loading, setLoading] = useState(() => !getBootPost(slug));
  // true only for transient/blocked failures (network, 5xx, robots-blocked) — never a real 404
  const [fetchFailed, setFetchFailed] = useState(false);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    setFetchFailed(false);
    // Prerendered article pages already hold their data — skip the skeleton flash
    if (!getBootPost(slug)) setLoading(true);
    getPost(slug)
      .then((p) => {
        // Renamed post: the API points old slugs at the new one (301-equivalent)
        if (p && p.redirect) {
          navigate(`/blog/${p.redirect}`, { replace: true });
          return;
        }
        setPost(p);
        setLoading(false);
        if (Array.isArray(p.related_slugs) && p.related_slugs.length) {
          getPosts().then((list) => {
            const bySlug = new Map(list.map((x) => [x.slug, x]));
            setRelatedPosts(p.related_slugs.map((s) => bySlug.get(s)).filter(Boolean));
          });
        } else {
          setRelatedPosts([]);
        }
        window.scrollTo({ top: 0, behavior: "instant" });
      })
      .catch((err) => {
        setPost(null);
        setFetchFailed(!(err && err.status === 404));
        setLoading(false);
      });
  }, [slug]);

  if (loading) {
    return (
      <>
        {/* Keep the head completely empty during the loading race condition
            so the pre-renderer captures zero conflicting meta tags */}
        <Helmet>
          <title>Loading...</title>
        </Helmet>
        <Nav />
        <main className="pt-32 pb-20 min-h-screen container-x">
          <article className="max-w-3xl mx-auto animate-pulse" data-testid="post-loading">
            <div className="h-9 w-32 bg-[#EAE6DF] rounded-full mb-10" />
            <div className="h-3 w-28 bg-[#EAE6DF] rounded mb-5" />
            <div className="h-10 w-full bg-[#EAE6DF] rounded mb-3" />
            <div className="h-10 w-2/3 bg-[#EAE6DF] rounded mb-8" />
            <div className="rounded-3xl bg-[#EAE6DF] aspect-video mb-10" />
            <div className="space-y-3">
              {[0, 1, 2, 3, 4].map((s) => (
                <div key={s} className="h-4 bg-[#EAE6DF] rounded" style={{ width: `${92 - s * 7}%` }} />
              ))}
            </div>
          </article>
        </main>
      </>
    );
  }

  if (!post) {
    return (
      <>
        {/* Discard only genuinely missing pages (404). A failed/blocked fetch must never emit noindex. */}
        <Helmet>
          {!fetchFailed && <meta name="robots" content="noindex, nofollow" />}
          <title>{fetchFailed ? "Loading article · Rihai" : "Post not found · Rihai"}</title>
        </Helmet>
        <Nav />
        <main className="pt-40 pb-20 min-h-screen container-x">
          <div className="max-w-xl">
            <h1 className="font-serif-display text-4xl mb-4">
              {lang === "en" ? "Post not found" : "लेख नहीं मिला"}
            </h1>
            <p className="text-[#4A4640] mb-8">
              {lang === "en"
                ? "This article may have moved or been removed."
                : "यह लेख हटा दिया गया हो सकता है।"}
            </p>
            <button onClick={() => navigate("/blog")} className="btn-primary">
              {pick(T.blog.back, lang)}
            </button>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const title = decodeHtml(post.title);
  const description = post.meta_description || stripTags(post.excerpt).slice(0, 200);
  const publishedAt = post.published_at || (post.date ? `${post.date}T00:00:00+05:30` : undefined);
  const updatedAt = post.updated_at || publishedAt;

  // Internal linking — guides & cities matched to this post's content
  const matchText = `${title} ${post.excerpt || ""} ${post.content || ""}`;
  const relatedGuides = matchGuidesInText(matchText).slice(0, 2);
  const matchedCities = matchCitiesInText(matchText);
  const citiesToShow = matchedCities.length ? matchedCities : ALL_CITIES;

  return (
    <>
      <SEO
        title={post.seo_title || title}
        titleSuffix={post.seo_title ? "" : " | Rihai Legal Journal"}
        description={description}
        path={`/blog/${post.slug}`}
        image={post.og_image || post.cover || undefined}
        type="article"
        noindex={!!post.noindex}
        article={{
          publishedAt,
          modifiedAt: updatedAt,
          author: post.author_id || post.author || "Rihai Journal",
          category: post.category || "Article",
          tags: [post.primary_keyword, post.category].filter(Boolean),
        }}
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://myrihai.com/" },
              { "@type": "ListItem", position: 2, name: "Journal", item: "https://myrihai.com/blog" },
              { "@type": "ListItem", position: 3, name: title, item: `https://myrihai.com/blog/${post.slug}` },
            ],
          })}
        </script>
        {Array.isArray(post.faq) && post.faq.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: post.faq.map((f) => ({
                "@type": "Question",
                name: f.question,
                acceptedAnswer: { "@type": "Answer", text: f.answer },
              })),
            })}
          </script>
        )}
      </Helmet>
      <Nav />
      <main className="pt-32 pb-20">
        <div className="container-x">
          <article className="max-w-3xl mx-auto" data-testid="blog-post-view">
            <button
              onClick={() => navigate("/blog")}
              className="btn-secondary mb-10 text-sm"
              data-testid="blog-back"
            >
              <ArrowLeft size={16} /> {pick(T.blog.back, lang)}
            </button>
            <div className="overline mb-4">{post.category}</div>
            <h1
              className="font-serif-display serif-textured text-4xl sm:text-5xl font-light leading-tight mb-6"
              data-testid="blog-post-title"
            >
              {title}
            </h1>
            <div className="flex gap-5 text-sm text-[#736F66] mb-8">
              <span className="flex items-center gap-1.5">
                <Calendar size={14} /> {post.date}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock size={14} /> {post.read_time}
              </span>
              <span>· {post.author}</span>
            </div>

            {(post.updated_at || post.author_id || post.reviewer_id) && (
              <p className="text-sm text-[#736F66] -mt-4 mb-8" data-testid="post-byline">
                {post.updated_at && (
                  <>
                    Updated{" "}
                    <time dateTime={post.updated_at}>
                      {new Date(post.updated_at).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </time>
                  </>
                )}
                {post.author_id && ` · Written by ${post.author_id}`}
                {post.reviewer_id && ` · Legally reviewed by ${post.reviewer_id}`}
              </p>
            )}

            {post.tldr && (
              <div
                className="rounded-2xl border border-[#2C4C3B]/25 bg-[#2C4C3B]/5 p-6 mb-10"
                data-testid="post-tldr"
              >
                <div className="overline mb-2">TL;DR</div>
                <p className="text-[#1A1A1A] leading-relaxed">{post.tldr}</p>
              </div>
            )}

            <img
              src={post.cover || FALLBACK_COVER}
              alt={title}
              decoding="async"
              className="w-full rounded-3xl mb-10 aspect-video object-cover"
            />
            <div
              className="prose max-w-none text-[#4A4640] leading-relaxed text-lg blog-content"
              dangerouslySetInnerHTML={{ __html: post.content }}
              data-testid="blog-post-content"
            />

            {Array.isArray(post.faq) && post.faq.length > 0 && (
              <section className="mt-14" data-testid="post-faq">
                <h2 className="font-serif-display text-3xl font-medium mb-6">
                  {lang === "hi" ? "अक्सर पूछे जाने वाले सवाल" : "Frequently asked questions"}
                </h2>
                <div className="space-y-4">
                  {post.faq.map((f, i) => (
                    <div key={i} className="rounded-2xl border border-[#D8CDBA] p-5" data-testid={`post-faq-${i}`}>
                      <h3 className="font-medium text-[#1A1A1A] mb-2">{f.question}</h3>
                      <p className="text-[#4A4640] leading-relaxed text-[15px]">{f.answer}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {Array.isArray(post.sources) && post.sources.length > 0 && (
              <section className="mt-14 pt-8 border-t border-[#D8CDBA]" data-testid="post-sources">
                <div className="overline mb-4">{lang === "hi" ? "स्रोत" : "Sources"}</div>
                <ul className="space-y-2">
                  {post.sources.map((s, i) => (
                    <li key={i}>
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#2C4C3B] underline underline-offset-4 hover:text-[#1A1A1A]"
                      >
                        {s.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Contextual city CTA — shown when the post mentions a covered city */}
            {matchedCities.length > 0 && (
              <div
                className="mt-14 rounded-2xl border border-[#C46B2F]/40 bg-[#FAF3EC] p-6 flex flex-col sm:flex-row sm:items-center gap-4"
                data-testid="post-city-banner"
              >
                <p className="text-[#4A4640] leading-relaxed flex-1">
                  {lang === "hi"
                    ? `${matchedCities[0].cityHindi} में तुरंत ज़मानत चाहिए? बिचौलियों के बिना सीधे बात कीजिए।`
                    : `Need emergency bail in ${matchedCities[0].city}? Talk to someone directly — no middlemen.`}
                </p>
                <Link
                  to={`/${matchedCities[0].slug}`}
                  className="btn-primary text-sm whitespace-nowrap inline-flex items-center gap-1.5"
                  data-testid="post-city-banner-link"
                >
                  {lang === "hi"
                    ? `${matchedCities[0].cityHindi} में सत्यापित ज़मानत वकील`
                    : `Verified bail lawyer in ${matchedCities[0].city}`}
                  <ArrowRight size={14} />
                </Link>
              </div>
            )}

            {/* Related guides + city pages (automatic internal links) */}
            <section className="mt-14 pt-10 border-t border-[#D8CDBA]" data-testid="post-related">
              <div className="overline mb-5">
                {lang === "en" ? "Understand the process first" : "पहले प्रक्रिया समझिए"}
              </div>
              <div className="grid sm:grid-cols-2 gap-4 mb-10">
                {relatedGuides.map((g) => (
                  <Link
                    key={g.slug}
                    to={`/guide/${g.slug}`}
                    className="group rounded-2xl border border-[#D8CDBA] p-5 hover:border-[#2C4C3B] transition-colors"
                    data-testid={`post-guide-${g.slug}`}
                  >
                    <div className="text-xs uppercase tracking-widest text-[#C46B2F] mb-2">
                      {lang === "en" ? "Rihai Guide" : "Rihai गाइड"}
                    </div>
                    <div className="font-serif-display text-lg font-medium text-[#1A1A1A] leading-snug group-hover:text-[#2C4C3B] transition-colors">
                      {pick(g.title, lang)}
                    </div>
                  </Link>
                ))}
              </div>
              {relatedPosts.length > 0 && (
                <div className="mb-10" data-testid="post-related-reading">
                  <div className="overline mb-4">
                    {lang === "en" ? "Related reading" : "संबंधित लेख"}
                  </div>
                  <ul className="space-y-2.5">
                    {relatedPosts.map((rp) => (
                      <li key={rp.slug}>
                        <Link
                          to={`/blog/${rp.slug}`}
                          className="text-[#2C4C3B] underline underline-offset-4 hover:text-[#1A1A1A]"
                        >
                          {decodeHtml(rp.title)}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="overline mb-4">
                {lang === "en" ? "A verified bail lawyer in your city" : "आपके शहर में सत्यापित ज़मानत वकील"}
              </div>
              <div className="flex flex-wrap gap-2">
                {citiesToShow.map((ct) => (
                  <Link
                    key={ct.slug}
                    to={`/${ct.slug}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#D8CDBA] px-4 py-2 text-sm text-[#4A4640] hover:border-[#2C4C3B] hover:text-[#2C4C3B] transition-colors"
                    data-testid={`post-city-${ct.slug}`}
                  >
                    <MapPin size={12} strokeWidth={2} /> {lang === "hi" ? ct.cityHindi : ct.city}
                  </Link>
                ))}
              </div>
            </section>

            {/* Inline CTA */}
            <div
              className="mt-16 rounded-3xl bg-[#2C4C3B] text-white p-10 md:p-12 relative overflow-hidden"
              data-testid="blog-post-cta"
            >
              <div className="grain" />
              <div className="overline text-[#D8CDBA] mb-4 relative">
                {lang === "en" ? "Living this right now?" : "अभी यही झेल रहे हैं?"}
              </div>
              <h3 className="font-serif-display text-3xl md:text-4xl font-light leading-tight mb-4 relative">
                {lang === "en"
                  ? "Don't read alone. Talk to us."
                  : "अकेले मत पढ़िए। हमसे बात कीजिए।"}
              </h3>
              <p className="text-[#D8CDBA] max-w-lg mb-8 relative">
                {lang === "en"
                  ? "A verified bail lawyer, on a call — no middleman, no runaround."
                  : "एक सत्यापित ज़मानत वकील, फोन पर — कोई बिचौलिया नहीं, कोई भटकाव नहीं।"}
              </p>
              <div className="flex flex-wrap gap-3 relative">
                <Link
                  to="/#lead"
                  className="btn-primary bg-white text-[#2C4C3B] hover:bg-[#EAE6DF]"
                  data-testid="blog-post-cta-lead"
                >
                  {pick(T.hero.ctaPrimary, lang)} <ArrowRight size={16} />
                </Link>
                <a
                  href={`tel:${T.phone.number}`}
                  className="btn-secondary border-white text-white hover:bg-white hover:text-[#2C4C3B]"
                  data-testid="blog-post-cta-call"
                >
                  <Phone size={16} strokeWidth={2} /> {T.phone.display}
                </a>
              </div>
            </div>
          </article>
        </div>
      </main>
      <Footer />
    </>
  );
}
